<main class="intake-portal-page">
  <header class="intake-portal-page__header">
    <p class="intake-portal-page__eyebrow">{{ page_eyebrow }}</p>
    <h1 class="intake-portal-page__title">{{ page_title }}</h1>
    <p class="intake-portal-page__subtitle">
      {{ page_subtitle }}
    </p>
  </header>

  <section v-if="is_loading_form_page" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">Loading form</h2>
      <p class="intake-portal-card__subtitle">Fetching the assigned onboarding form.</p>
    </header>
  </section>

  <section v-else-if="has_multiple_forms" class="intake-portal-preview-switcher">
    <div class="intake-portal-preview-switcher__inner">
      <div class="intake-portal-preview-switcher__group intake-portal-preview-switcher__group--form">
        <label for="queue_form_select" class="intake-portal-preview-switcher__label">Form</label>
        <div class="queue-form-tabs" role="tablist" aria-label="Assigned forms">
          <button
            v-for="form in application_forms"
            :key="form.id"
            type="button"
            class="queue-form-tab"
            :class="{ 'is-active': String(form.id) === String(selected_application_form_id) }"
            @click="select_application_form(form.id)"
          >
            {{ form.form_name }}
          </button>
        </div>
      </div>
    </div>
  </section>

  <div v-if="form_page_error" class="alert alert-warning intake-portal-alert" role="alert">
    {{ form_page_error }}
  </div>

  <div v-if="submit_success_message" class="alert alert-success intake-portal-alert" role="alert">
    {{ submit_success_message }}
  </div>

  <div v-if="should_show_workflow_navigation" class="card border rounded-3 mb-4">
    <div class="card-body py-3">
      <div class="workflow-stepper">
        <div
          v-for="(step, index) in workflow_journey_steps"
          :key="step.id"
          class="workflow-stepper__item"
        >
          <div class="workflow-stepper__content">
            <span
              :class="[
                'rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0',
                'workflow-step-circle',
                step.state === 'completed'
                  ? 'bg-success text-white'
                  : step.state === 'current'
                    ? 'border border-success text-success bg-white'
                    : step.state === 'previous'
                      ? 'border border-secondary-subtle text-secondary bg-light'
                      : 'bg-light text-muted'
              ]"
            >
              <i v-if="step.state === 'completed'" class="bi bi-check-lg" aria-hidden="true"></i>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span :class="['workflow-stepper__label fw-semibold', step.state === 'upcoming' ? 'text-muted' : 'text-black']">{{ step.label }}</span>
            <span class="workflow-stepper__meta small text-muted">
              {{ step.state === 'completed' ? 'Completed' : step.state === 'current' ? 'Current' : step.state === 'previous' ? 'Previous' : 'Upcoming' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <section v-if="should_show_workflow_navigation && workflow_content_tabs.length" class="intake-portal-card mb-4 overflow-hidden">
    <div class="onboarding-gate-audit-content">
      <nav class="onboarding-gate-shell-tabs" aria-label="Workflow sections">
        <button
          v-for="tab in workflow_shell_tabs"
          :key="tab.id"
          type="button"
          :class="['onboarding-gate-shell-tabs__tab', { 'is-active': active_workflow_shell_tab === tab.id }]"
          @click="set_workflow_shell_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.badge" class="badge rounded-pill">{{ tab.badge }}</span>
        </button>
      </nav>

      <div :class="{ 'onboarding-history-split': active_workflow_shell_tab === 'history' }">
        <template v-if="is_current_workflow_tab">
          <section v-if="is_documents_gate_flow" class="intake-portal-card" aria-labelledby="queue_form_documents_title">
            <header class="intake-portal-card__header">
              <div class="queue-form-card-heading">
                <div class="queue-form-card-heading__row">
                  <h2 id="queue_form_documents_title" class="intake-portal-card__title">{{ documents_gate_title }}</h2>
                  <span v-if="history_document_items.length" class="badge rounded-pill text-bg-light border">{{ history_document_items.length }}</span>
                </div>
                <p class="intake-portal-card__subtitle">{{ documents_gate_subtitle }}</p>
              </div>
            </header>

            <div class="px-3 pb-3">
              <section class="border rounded-3 bg-light-subtle px-4 py-3 mb-4" aria-label="Current document action">
                <div class="text-uppercase text-muted fw-semibold small mb-2">Required Action</div>
                <p class="text-muted mb-0">{{ workflow_current_action_copy }}</p>
              </section>

              <div
                v-if="history_document_review_feedback"
                :class="[
                  'alert mb-4 py-2 px-3 small',
                  history_document_review_feedback_tone === 'success'
                    ? 'alert-success'
                    : history_document_review_feedback_tone === 'danger'
                      ? 'alert-danger'
                      : 'alert-warning'
                ]"
                role="alert"
              >
                {{ history_document_review_feedback }}
              </div>

              <div v-if="history_document_items.length" class="row g-3">
                <div
                  v-for="document in history_document_items"
                  :key="document.id"
                  class="col-12"
                >
                  <article class="border rounded-3 p-3 h-100">
                    <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div class="fw-semibold">{{ document.label }}</div>
                        <div class="small text-muted">{{ document.file_name }}</div>
                      </div>
                      <span class="badge rounded-pill text-bg-light border">{{ document.status }}</span>
                    </div>

                    <div class="d-flex align-items-center gap-2 flex-wrap mt-3">
                      <button
                        v-if="document.url"
                        type="button"
                        class="onboarding-queue-view-button"
                        @click="open_history_document_preview(document)"
                      >
                        <span>Preview</span>
                        <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                      </button>
                      <button
                        v-if="is_history_document_review_supported(document) && !is_history_document_review_approved(document)"
                        type="button"
                        class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-2"
                        :disabled="is_history_document_review_busy(document)"
                        @click="run_history_document_review(document, false)"
                      >
                        {{ is_history_document_review_busy(document) ? 'Saving...' : 'Reject' }}
                      </button>
                      <button
                        v-if="is_history_document_review_supported(document) && !is_history_document_review_approved(document)"
                        type="button"
                        class="btn btn-sm btn-success d-inline-flex align-items-center gap-2"
                        :disabled="is_history_document_review_busy(document)"
                        @click="run_history_document_review(document, true)"
                      >
                        {{ is_history_document_review_busy(document) ? 'Saving...' : 'Approve' }}
                      </button>
                    </div>

                    <div
                      v-if="!is_history_document_review_approved(document) || document.verification_comment"
                      class="mt-3 pt-3 border-top"
                    >
                      <template v-if="is_history_document_review_approved(document)">
                        <div v-if="document.verification_comment" class="small text-muted">
                          Comment: {{ document.verification_comment }}
                        </div>
                      </template>
                      <template v-else>
                        <div class="small text-muted mb-2">{{ history_document_review_status_label(document) }}</div>
                        <textarea
                          class="form-control form-control-sm mb-3"
                          rows="2"
                          :disabled="is_history_document_review_busy(document) || !is_history_document_review_supported(document)"
                          :value="history_document_review_comment(document)"
                          @input="set_history_document_review_comment(document, $event.target.value)"
                          placeholder="Add an optional review comment"
                        ></textarea>
                        <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                          <div
                            v-if="!is_history_document_review_supported(document)"
                            class="small text-muted"
                          >
                            {{ unsupported_history_document_review_reason(document) }}
                          </div>
                          <div
                            v-else-if="document.verification_comment"
                            class="small text-muted"
                          >
                            Latest review note saved.
                          </div>
                        </div>
                      </template>
                    </div>
                  </article>
                </div>
              </div>

              <div v-else class="text-muted">
                No uploaded documents were returned for this application.
              </div>
            </div>
          </section>

          <template v-else>
          <div v-if="intake_steps.length" class="queue-form-steps-shell mb-4">
            <button
              type="button"
              class="queue-form-steps-shell__arrow queue-form-steps-shell__arrow--left"
              aria-label="Go to previous section"
              :disabled="current_step === 1"
              @click="go_to_previous_step"
            >
              <i class="bi bi-chevron-left"></i>
            </button>

            <nav ref="stepsScroller" class="intake-portal-steps queue-form-steps-nav" aria-label="Assigned onboarding steps">
              <ol class="intake-portal-steps__list">
              <li
                v-for="step in intake_steps"
                :key="step.id"
                :class="['intake-portal-steps__item', 'queue-form-steps__item', { 'is-active': is_active_step(step) }]"
                :data-step-number="step.number"
              >
                <button
                  type="button"
                  class="queue-form-steps__button"
                  @click="go_to_step(step.number)"
                >
                  <span class="intake-portal-steps__number">{{ step.number }}</span>
                  <span class="intake-portal-steps__label">{{ step.label }}</span>
                </button>
              </li>
              </ol>
            </nav>

            <button
              type="button"
              class="queue-form-steps-shell__arrow queue-form-steps-shell__arrow--right"
              aria-label="Go to next section"
              :disabled="current_step >= intake_steps.length"
              @click="go_to_next_step"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <section v-if="current_main_section" class="intake-portal-card" aria-labelledby="queue_form_step_title">
            <header class="intake-portal-card__header">
              <div class="queue-form-card-heading">
                <div class="queue-form-card-heading__row">
                  <h4 id="queue_form_step_title" class="intake-portal-card__title">{{ card_title }}</h4>
                  <span v-if="applicant_name" class="queue-form-applicant-badge">
                    <span class="queue-form-applicant-badge__value">{{ applicant_name }}</span>
                  </span>
                </div>
                <p class="intake-portal-card__subtitle">{{ card_subtitle }}</p>
              </div>
              </header>

                <form class="intake-portal-form" @submit.prevent="submit_or_continue">
                  <div
                    v-if="Array.isArray(current_form_instructions) && current_form_instructions.length"
                    class="border rounded-3 bg-light-subtle px-3 py-3 mb-4"
                  >
                    <p
                      v-for="(instruction, index) in current_form_instructions"
                      :key="`form-instruction-${index}`"
                      :class="['small text-muted mb-0 queue-form-instruction-text', { 'mt-2': index > 0 }]"
                    >
                      {{ instruction }}
                    </p>
                  </div>

                <div
                  v-for="sub_section in current_main_section.sub_sections"
                  :key="sub_section.instance_id || sub_section.id"
                class="intake-portal-section-block"
                v-show="can_view_sub_section(sub_section)"
              >
                <div
                  v-if="should_show_sub_section_title(sub_section) || can_duplicate_sub_section(sub_section) || can_remove_sub_section(sub_section)"
                  class="intake-portal-section-block__header d-flex justify-content-between align-items-start gap-3 flex-wrap"
                >
                    <div>
                      <h3 v-if="should_show_sub_section_title(sub_section)" class="intake-portal-section-block__title">
                        {{ format_sub_section_title(sub_section) }}
                      </h3>
                      <p
                        v-if="sub_section_access_note(sub_section) && !(Array.isArray(sub_section.instructions) && sub_section.instructions.length)"
                        class="intake-portal-field__hint mb-0"
                      >
                        {{ sub_section_access_note(sub_section) }}
                      </p>
                    </div>
                  <div class="d-flex align-items-center gap-2">
                    <button
                      v-if="can_remove_sub_section(sub_section)"
                      type="button"
                      class="btn btn-light border rounded-3 px-3 py-2 text-xs fw-semibold d-inline-flex align-items-center"
                      :disabled="is_submitting"
                      @click="remove_sub_section_instance(sub_section)"
                    >
                      Remove
                    </button>
                    <button
                      v-if="can_duplicate_sub_section(sub_section)"
                      type="button"
                      class="btn btn-primary-dark rounded-3 px-3 py-2 text-xs fw-semibold shadow-sm d-inline-flex align-items-center"
                      :disabled="is_submitting"
                      @click="add_sub_section_instance(sub_section)"
                    >
                      Add another
                    </button>
                  </div>
                </div>

                  <div
                    v-if="Array.isArray(sub_section.instructions) && sub_section.instructions.length"
                    class="border rounded-3 px-3 py-3 mt-2 mb-3 ms-md-3 border-start border-3 queue-form-section-card"
                  >
                    <div class="mb-3">
                      <div class="fw-semibold text-success mb-0">Before you start this section</div>
                    </div>
                    <div class="row row-cols-1 row-cols-lg-2 g-2">
                      <div
                        v-for="(instruction, index) in sub_section.instructions"
                        :key="`${sub_section.instance_id || sub_section.id}-instruction-${index}`"
                        class="col"
                      >
                        <p class="small text-muted mb-0 queue-form-instruction-text">
                          {{ instruction }}
                        </p>
                      </div>
                    </div>
                    <p
                      v-if="sub_section_access_note(sub_section)"
                      class="small text-muted pt-3 mt-3 mb-0 queue-form-section-note"
                    >
                      {{ sub_section_access_note(sub_section) }}
                    </p>
                  </div>

                  <div class="intake-portal-form__grid">
                  <div
                    v-for="question in visible_questions_for_sub_section(sub_section)"
                    :key="question.id"
                    :class="[
                      'intake-portal-field',
                      {
                        'intake-portal-field--full': uses_textarea(question) || uses_checkbox_group(question) || is_dependent_question(question),
                        'intake-portal-field--dependent': is_dependent_question(question),
                        'queue-form-field--file': uses_file(question)
                      }
                    ]"
                  >
                    <div :class="['intake-portal-field__shell', { 'intake-portal-field__shell--dependent': is_dependent_question(question) }]">
                      <div v-if="is_dependent_question(question)" class="intake-portal-dependent-callout">
                        <span class="intake-portal-dependent-callout__line" aria-hidden="true"></span>
                        <div class="intake-portal-dependent-card">
                          <label
                            :for="`question_${question.id}`"
                            :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                          >
                            <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                            <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                              <i class="fa-solid fa-plus" aria-hidden="true"></i>
                            </span>
                          </label>

                          <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                          <textarea
                            v-if="uses_textarea(question)"
                            :id="`question_${question.id}`"
                            v-model="form_values[question.id]"
                            rows="4"
                            class="intake-portal-field__control intake-portal-field__control--textarea"
                            :disabled="is_question_read_only(sub_section)"
                          ></textarea>

                          <select
                            v-else-if="uses_select(question)"
                            :id="`question_${question.id}`"
                            v-model="form_values[question.id]"
                            class="intake-portal-field__control"
                            :disabled="is_question_read_only(sub_section)"
                          >
                            <option value="">Select an option</option>
                            <option v-for="option in question.options" :key="option.id" :value="option.value">
                              {{ option.label }}
                            </option>
                          </select>

                          <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                            <label
                              v-for="option in question.options"
                              :key="option.id"
                              class="intake-portal-choice"
                            >
                              <input
                                v-model="form_values[question.id]"
                                type="radio"
                                :name="`question_${question.id}`"
                                :value="option.value"
                                :disabled="is_question_read_only(sub_section)"
                              >
                              <span>{{ option.label }}</span>
                            </label>
                          </div>

                          <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                            <label
                              v-for="option in question.options"
                              :key="option.id"
                              class="intake-portal-choice"
                            >
                              <input
                                :checked="get_question_value(question).includes(option.value)"
                                type="checkbox"
                                :disabled="is_question_read_only(sub_section)"
                                @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                              >
                              <span>{{ option.label }}</span>
                            </label>
                          </div>

                          <FacialVerificationField
                            v-else-if="uses_facial_verification(question)"
                            :application-form-id="Number(selected_application_form_id || 0)"
                            :question="question"
                            :model-value="get_facial_verification_value(question.id)"
                            @update:modelValue="update_facial_verification_value(question.id, $event)"
                          />

                          <div
                            v-else-if="uses_file(question)"
                            class="intake-portal-file queue-form-file"
                          >
                            <label
                              :for="`question_${question.id}`"
                              class="queue-form-file__chooser"
                            >
                              <input
                                :id="`question_${question.id}`"
                                type="file"
                                class="d-none"
                                :disabled="is_question_read_only(sub_section)"
                                multiple
                                @change="handle_file_change(question.id, $event)"
                              >
                              <span class="intake-portal-file__button">Choose files</span>
                              <span class="intake-portal-file__name">{{ file_input_label(question.id) }}</span>
                            </label>

                            <div class="queue-form-file__actions" v-if="visible_question_files(question.id).length">
                              <div
                                v-for="(file, file_index) in visible_question_files(question.id)"
                                :key="`${question.id}_${file.id || file.name || file_index}`"
                                class="queue-form-file__row"
                              >
                                <span class="queue-form-file__status">
                                  <i class="bi bi-check-circle-fill"></i>
                                  <span>{{ format_file_name(file) }}</span>
                                </span>
                                <div class="queue-form-file__row-actions">
                                  <button
                                    type="button"
                                    class="queue-form-file__preview-button"
                                    @click.prevent="open_preview_modal(question, file)"
                                  >
                                    Preview
                                  </button>
                                  <button
                                    type="button"
                                    class="queue-form-file__preview-button"
                                    :disabled="is_question_read_only(sub_section)"
                                    @click.prevent="remove_question_file(question.id, file_index)"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                            <input
                              v-model="form_values[question.id]"
                              type="checkbox"
                              :disabled="is_question_read_only(sub_section)"
                            >
                            <span>Yes</span>
                          </label>

                          <input
                            v-else
                            :id="`question_${question.id}`"
                            v-model="form_values[question.id]"
                            :type="question_input_type(question)"
                            class="intake-portal-field__control"
                            :disabled="is_question_read_only(sub_section)"
                          />
                        </div>
                      </div>

                      <template v-else>
                        <label
                          :for="`question_${question.id}`"
                          :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                        >
                          <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                          <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                            <i class="fa-solid fa-plus" aria-hidden="true"></i>
                          </span>
                        </label>

                        <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                        <textarea
                          v-if="uses_textarea(question)"
                          :id="`question_${question.id}`"
                          v-model="form_values[question.id]"
                          rows="4"
                          class="intake-portal-field__control intake-portal-field__control--textarea"
                          :disabled="is_question_read_only(sub_section)"
                        ></textarea>

                        <select
                          v-else-if="uses_select(question)"
                          :id="`question_${question.id}`"
                          v-model="form_values[question.id]"
                          class="intake-portal-field__control"
                          :disabled="is_question_read_only(sub_section)"
                        >
                          <option value="">Select an option</option>
                          <option v-for="option in question.options" :key="option.id" :value="option.value">
                            {{ option.label }}
                          </option>
                        </select>

                        <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                          <label
                            v-for="option in question.options"
                            :key="option.id"
                            class="intake-portal-choice"
                          >
                            <input
                              v-model="form_values[question.id]"
                              type="radio"
                              :name="`question_${question.id}`"
                              :value="option.value"
                              :disabled="is_question_read_only(sub_section)"
                            >
                            <span>{{ option.label }}</span>
                          </label>
                        </div>

                        <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                          <label
                            v-for="option in question.options"
                            :key="option.id"
                            class="intake-portal-choice"
                          >
                            <input
                              :checked="get_question_value(question).includes(option.value)"
                              type="checkbox"
                              :disabled="is_question_read_only(sub_section)"
                              @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                            >
                            <span>{{ option.label }}</span>
                          </label>
                        </div>

                        <FacialVerificationField
                          v-else-if="uses_facial_verification(question)"
                          :application-form-id="Number(selected_application_form_id || 0)"
                          :question="question"
                          :model-value="get_facial_verification_value(question.id)"
                          @update:modelValue="update_facial_verification_value(question.id, $event)"
                        />

                        <div
                          v-else-if="uses_file(question)"
                          class="intake-portal-file queue-form-file"
                        >
                          <label
                            :for="`question_${question.id}`"
                            class="queue-form-file__chooser"
                          >
                            <input
                              :id="`question_${question.id}`"
                              type="file"
                              class="d-none"
                              :disabled="is_question_read_only(sub_section)"
                              multiple
                              @change="handle_file_change(question.id, $event)"
                            >
                            <span class="intake-portal-file__button">Choose files</span>
                            <span class="intake-portal-file__name">{{ file_input_label(question.id) }}</span>
                          </label>

                          <div class="queue-form-file__actions" v-if="visible_question_files(question.id).length">
                            <div
                              v-for="(file, file_index) in visible_question_files(question.id)"
                              :key="`${question.id}_${file.id || file.name || file_index}`"
                              class="queue-form-file__row"
                            >
                              <span class="queue-form-file__status">
                                <i class="bi bi-check-circle-fill"></i>
                                <span>{{ format_file_name(file) }}</span>
                              </span>
                              <div class="queue-form-file__row-actions">
                                <button
                                  type="button"
                                  class="queue-form-file__preview-button"
                                  @click.prevent="open_preview_modal(question, file)"
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  class="queue-form-file__preview-button"
                                  :disabled="is_question_read_only(sub_section)"
                                  @click.prevent="remove_question_file(question.id, file_index)"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                          <input
                            v-model="form_values[question.id]"
                            type="checkbox"
                            :disabled="is_question_read_only(sub_section)"
                          >
                          <span>Yes</span>
                        </label>

                        <input
                          v-else
                          :id="`question_${question.id}`"
                          v-model="form_values[question.id]"
                          :type="question_input_type(question)"
                          class="intake-portal-field__control"
                          :disabled="is_question_read_only(sub_section)"
                        />
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <footer class="intake-portal-card__footer">
                <button
                  type="button"
                  class="intake-portal-card__back"
                  :disabled="current_step === 1 || is_submitting"
                  @click="go_to_previous_step"
                >
                  Back
                </button>

                <span class="intake-portal-card__step">{{ current_step_label }}</span>

                <button type="submit" class="intake-portal-card__continue" :disabled="is_submitting || !can_submit_current_form">
                  <span v-if="!can_submit_current_form">View only</span>
                  <span v-else>{{ is_submitting ? 'Saving...' : submit_button_label }}</span>
                  <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </footer>
            </form>
          </section>

          <section v-else class="intake-portal-card">
            <header class="intake-portal-card__header">
              <h2 class="intake-portal-card__title">No form available</h2>
              <p class="intake-portal-card__subtitle">
                This applicant does not have an assigned onboarding form yet.
              </p>
            </header>
          </section>
          </template>
        </template>

        <section v-else-if="is_application_reference_tab" class="intake-portal-card">
          <header class="intake-portal-card__header">
            <div class="queue-form-card-heading">
              <div class="queue-form-card-heading__row">
                <h2 class="intake-portal-card__title">Application Reference</h2>
                <span class="badge rounded-pill text-bg-light border">Reference</span>
              </div>
              <p class="intake-portal-card__subtitle">Read-only reference for the submitted application details.</p>
            </div>
          </header>

          <div class="px-3 pb-3">
            <ApplicationDetailsReview :form_sections="onboarding_history_form_sections" />
          </div>
        </section>

        <section v-if="active_workflow_shell_tab === 'history'" class="intake-portal-card onboarding-history-rail-section" aria-labelledby="queue_form_history_title">
          <header class="intake-portal-card__header">
            <div class="queue-form-card-heading">
              <div class="queue-form-card-heading__row">
                <h2 id="queue_form_history_title" class="intake-portal-card__title">Onboarding History</h2>
                <span class="badge rounded-pill text-bg-light border">{{ workflow_history_tabs.length }}</span>
              </div>
              <p class="intake-portal-card__subtitle">Previous gates completed for this case.</p>
            </div>
          </header>

          <div class="px-3 pb-3">
            <div v-if="workflow_history_tabs.length" class="onboarding-gate-audit-list">
              <button
                v-for="(step, index) in workflow_history_tabs"
                :key="step.id"
                type="button"
                :class="['onboarding-gate-audit-item', { 'is-active': active_workflow_tab_step?.gate_id === step.gate_id, 'is-completed': step.state === 'completed', 'is-current': step.state === 'current' }]"
                @click="set_workflow_content_tab(step)"
              >
                <span class="onboarding-gate-audit-item__marker" aria-hidden="true">
                  <i v-if="step.state === 'completed'" class="bi bi-check-lg"></i>
                  <span v-else>{{ index + 1 }}</span>
                </span>
                <span class="onboarding-gate-audit-item__content">
                  <span class="onboarding-gate-audit-item__title">{{ step.label }}</span>
                  <span class="onboarding-gate-audit-item__status" :class="{ 'is-current': step.state === 'current' }">
                    {{ step.badge || (step.state === 'completed' ? 'Completed' : 'Previous') }}
                  </span>
                  <span class="onboarding-gate-audit-item__detail">Read-only view of this workflow step.</span>
                </span>
              </button>
            </div>
            <div v-else class="text-muted">No previous gates have been completed for this case yet.</div>
          </div>
        </section>

        <section v-if="active_workflow_shell_tab === 'history' && active_history_step" class="intake-portal-card onboarding-history-detail-placeholder" aria-labelledby="queue_form_history_title">
          <header class="intake-portal-card__header">
            <div class="queue-form-card-heading">
              <div class="queue-form-card-heading__row">
                <h2 id="queue_form_history_title" class="intake-portal-card__title">{{ active_history_step.label }}</h2>
                <span class="badge rounded-pill text-bg-light border">{{ active_history_step_badge_label }}</span>
              </div>
              <p class="intake-portal-card__subtitle">{{ active_history_step_description }}</p>
            </div>
          </header>

          <div v-if="history_panel_loading" class="text-muted px-3 pb-3">
            Loading previous step history...
          </div>

          <div v-else-if="history_panel_error" class="alert alert-warning mx-3 mb-3" role="alert">
            {{ history_panel_error }}
          </div>

          <div v-else-if="active_history_gate_id === 'manual-verification'" class="px-3 pb-3">
            <ManualVerificationHistoryView
              v-if="history_manual_run"
              :decision-action="history_manual_decision_action()"
              :decision-actor="active_history_actor"
              :decision-comment="history_manual_run.decision_reason || ''"
              :decision-label="history_manual_decision_label()"
              :decision-tone="history_manual_decision_action() === 'approve' ? 'success' : history_manual_decision_action() === 'request_changes' ? 'warning' : history_manual_decision_action() === 'reject' ? 'danger' : 'secondary'"
              :decision-timestamp="active_history_completed_at"
              :status-label="format_history_value(history_manual_run.status)"
              :review-sections="history_manual_review_sections"
              :applicant-form-sections="onboarding_history_form_sections"
              :documents="history_document_items"
              :show-history-tabs="false"
            />

            <div v-else class="text-muted">
              No manual verification history was found for this completed step.
            </div>
          </div>

          <div v-else class="px-3 pb-3">
            <div v-if="active_history_gate_id !== 'verification'" class="border rounded-3 p-3 mb-3 bg-light-subtle">
              <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <div class="fw-semibold">Completed by: {{ active_history_actor }}</div>
                  <div class="small text-muted">Date: {{ format_history_date(active_history_completed_at) }}</div>
                </div>
                <span :class="['badge rounded-pill', active_history_status_tone === 'success' ? 'text-bg-success' : active_history_status_tone === 'warning' ? 'text-bg-warning' : active_history_status_tone === 'danger' ? 'text-bg-danger' : 'text-bg-secondary']">
                  {{ active_history_action_label }}
                </span>
              </div>
              <div class="small text-muted mt-2">{{ active_history_reason }}</div>
            </div>

            <div v-if="active_history_gate_id === 'verification'" class="mb-4">
              <HistoricalVerificationChecksView
                :checks="history_auto_verification_checks"
                empty-message="No verification checks were returned for this case."
                :is-expanded="history_verification_check_expanded"
                :get-check-passed-chips="get_check_passed_chips"
                :get-check-result-detail-rows="get_check_result_detail_rows"
                :get-check-history="get_check_history"
                :status-badge-class="status_badge_class"
                :evidence-icon-class="evidence_icon_class"
                :field-result-row-class="field_result_row_class"
                @toggle-check="toggle_history_verification_check_expanded"
              />
            </div>

            <div v-if="active_history_gate_id === 'e-signing'" class="mb-4">
              <h3 class="h6 fw-semibold mb-3">E-signing Documents</h3>
              <div v-if="history_agreement_items.length" class="row g-3">
                <div
                  v-for="agreement in history_agreement_items"
                  :key="agreement.id"
                  class="col-12 col-xl-6"
                >
                  <article class="border rounded-3 p-3 h-100">
                    <div class="fw-semibold mb-1">{{ agreement.name }}</div>
                    <div class="small text-muted mb-2">{{ agreement.status }}</div>
                    <div class="small"><strong>Sent on:</strong> {{ format_history_date(agreement.sent_on) }}</div>
                    <div class="small" v-if="agreement.signer"><strong>Signer:</strong> {{ agreement.signer }}</div>
                    <a v-if="agreement.url" :href="agreement.url" target="_blank" rel="noopener noreferrer" class="small fw-semibold d-inline-block mt-2">
                      Open document
                    </a>
                  </article>
                </div>
              </div>
              <div v-else class="text-muted">
                No e-signing documents were recorded for this completed step.
              </div>
            </div>

            <div v-if="active_history_gate_id !== 'verification' && should_show_history_supporting_context && history_document_items.length" class="mb-4">
              <h3 class="h6 fw-semibold mb-3">Submitted Documents</h3>
              <div class="row g-3">
                <div
                  v-for="document in history_document_items"
                  :key="document.id"
                  class="col-12 col-xl-6"
                >
                  <article class="border rounded-3 p-3 h-100">
                    <div class="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div class="fw-semibold">{{ document.label }}</div>
                        <div class="small text-muted">{{ document.file_name }}</div>
                      </div>
                      <span class="badge rounded-pill text-bg-light border">{{ document.status }}</span>
                    </div>
                    <a v-if="document.url" :href="document.url" target="_blank" rel="noopener noreferrer" class="small fw-semibold d-inline-block mt-2">
                      Open document
                    </a>
                  </article>
                </div>
              </div>
            </div>

            <div v-if="!should_show_history_application_details && !should_show_history_supporting_context && active_history_gate_id !== 'verification' && active_history_gate_id !== 'e-signing'" class="text-muted">
              No additional read-only details were recorded for this completed step.
            </div>
          </div>
        </section>
        <section v-if="active_workflow_shell_tab === 'history' && !active_history_step" class="intake-portal-card onboarding-history-detail-placeholder">
          <div class="px-3 py-4 text-muted">Select a completed gate to view its details.</div>
        </section>
      </div>
    </div>
  </section>

  <div v-if="!should_show_workflow_navigation && is_current_workflow_tab && intake_steps.length" class="queue-form-steps-shell">
    <button
      type="button"
      class="queue-form-steps-shell__arrow queue-form-steps-shell__arrow--left"
      aria-label="Go to previous section"
      :disabled="current_step === 1"
      @click="go_to_previous_step"
    >
      <i class="bi bi-chevron-left"></i>
    </button>

    <nav ref="stepsScroller" class="intake-portal-steps queue-form-steps-nav" aria-label="Assigned onboarding steps">
      <ol class="intake-portal-steps__list">
      <li
        v-for="step in intake_steps"
        :key="step.id"
        :class="['intake-portal-steps__item', 'queue-form-steps__item', { 'is-active': is_active_step(step) }]"
        :data-step-number="step.number"
      >
        <button
          type="button"
          class="queue-form-steps__button"
          @click="go_to_step(step.number)"
        >
          <span class="intake-portal-steps__number">{{ step.number }}</span>
          <span class="intake-portal-steps__label">{{ step.label }}</span>
        </button>
      </li>
      </ol>
    </nav>

    <button
      type="button"
      class="queue-form-steps-shell__arrow queue-form-steps-shell__arrow--right"
      aria-label="Go to next section"
      :disabled="current_step >= intake_steps.length"
      @click="go_to_next_step"
    >
      <i class="bi bi-chevron-right"></i>
    </button>
  </div>

  <section v-if="!should_show_workflow_navigation && is_current_workflow_tab && current_main_section" class="intake-portal-card" aria-labelledby="queue_form_step_title">
    <header class="intake-portal-card__header">
      <div class="queue-form-card-heading">
        <div class="queue-form-card-heading__row">
          <h4 id="queue_form_step_title" class="intake-portal-card__title">{{ card_title }}</h4>
          <span v-if="applicant_name" class="queue-form-applicant-badge">
            <span class="queue-form-applicant-badge__value">{{ applicant_name }}</span>
          </span>
        </div>
        <p class="intake-portal-card__subtitle">{{ card_subtitle }}</p>
      </div>
      </header>

        <form class="intake-portal-form" @submit.prevent="submit_or_continue">
            <div
              v-if="Array.isArray(current_form_instructions) && current_form_instructions.length"
              class="border rounded-3 bg-light-subtle px-3 py-3 mb-4"
            >
              <p
                v-for="(instruction, index) in current_form_instructions"
                :key="`form-instruction-compact-${index}`"
                :class="['small text-muted mb-0 queue-form-instruction-text', { 'mt-2': index > 0 }]"
              >
                {{ instruction }}
              </p>
            </div>

          <div
            v-for="sub_section in current_main_section.sub_sections"
            :key="sub_section.instance_id || sub_section.id"
          class="intake-portal-section-block"
          v-show="can_view_sub_section(sub_section)"
        >
          <div
            v-if="should_show_sub_section_title(sub_section) || can_duplicate_sub_section(sub_section) || can_remove_sub_section(sub_section)"
            class="intake-portal-section-block__header d-flex justify-content-between align-items-start gap-3 flex-wrap"
          >
              <div>
                <h3 v-if="should_show_sub_section_title(sub_section)" class="intake-portal-section-block__title">
                  {{ format_sub_section_title(sub_section) }}
                </h3>
                <p
                  v-if="sub_section_access_note(sub_section) && !(Array.isArray(sub_section.instructions) && sub_section.instructions.length)"
                  class="intake-portal-field__hint mb-0"
                >
                  {{ sub_section_access_note(sub_section) }}
                </p>
              </div>
            <div class="d-flex align-items-center gap-2">
              <button
                v-if="can_remove_sub_section(sub_section)"
                type="button"
                class="btn btn-light border rounded-3 px-3 py-2 text-xs fw-semibold d-inline-flex align-items-center"
                :disabled="is_submitting"
                @click="remove_sub_section_instance(sub_section)"
              >
                Remove
              </button>
              <button
                v-if="can_duplicate_sub_section(sub_section)"
                type="button"
                class="btn btn-primary-dark rounded-3 px-3 py-2 text-xs fw-semibold shadow-sm d-inline-flex align-items-center"
                :disabled="is_submitting"
                @click="add_sub_section_instance(sub_section)"
              >
                Add another
              </button>
            </div>
          </div>

          <div
            v-if="Array.isArray(sub_section.instructions) && sub_section.instructions.length"
            class="border rounded-3 px-3 py-3 mt-2 mb-3 ms-md-3 border-start border-3 queue-form-section-card"
          >
            <div class="mb-3">
              <div class="fw-semibold text-success mb-0 text-x">Before you start this section</div>
            </div>
            <div class="row row-cols-1 row-cols-lg-2 g-2">
              <div
                v-for="(instruction, index) in sub_section.instructions"
                :key="`${sub_section.instance_id || sub_section.id}-compact-instruction-${index}`"
                class="col"
              >
                <p class="small text-muted mb-0 text-xs queue-form-instruction-text">
                  {{ instruction }}
                </p>
              </div>
            </div>
            <p
              v-if="sub_section_access_note(sub_section)"
              class="small text-muted pt-3 mt-3 mb-0 queue-form-section-note"
            >
              {{ sub_section_access_note(sub_section) }}
            </p>
          </div>

          <div class="intake-portal-form__grid">
          <div
            v-for="question in visible_questions_for_sub_section(sub_section)"
            :key="question.id"
            :class="[
              'intake-portal-field',
              {
                'intake-portal-field--full': uses_textarea(question) || uses_checkbox_group(question) || is_dependent_question(question),
                'intake-portal-field--dependent': is_dependent_question(question),
                'queue-form-field--file': uses_file(question)
              }
            ]"
          >
            <div :class="['intake-portal-field__shell', { 'intake-portal-field__shell--dependent': is_dependent_question(question) }]">
              <div v-if="is_dependent_question(question)" class="intake-portal-dependent-callout">
                <span class="intake-portal-dependent-callout__line" aria-hidden="true"></span>
                <div class="intake-portal-dependent-card">
                  <label
                    :for="`question_${question.id}`"
                    :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                  >
                    <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                    <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                      <i class="fa-solid fa-plus" aria-hidden="true"></i>
                    </span>
                  </label>

                  <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                  <textarea
                    v-if="uses_textarea(question)"
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    rows="4"
                    class="intake-portal-field__control intake-portal-field__control--textarea"
                    :disabled="is_question_read_only(sub_section)"
                  ></textarea>

                  <select
                    v-else-if="uses_select(question)"
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    class="intake-portal-field__control"
                    :disabled="is_question_read_only(sub_section)"
                  >
                    <option value="">Select an option</option>
                    <option v-for="option in question.options" :key="option.id" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>

                  <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                    <label
                      v-for="option in question.options"
                      :key="option.id"
                      class="intake-portal-choice"
                    >
                      <input
                        v-model="form_values[question.id]"
                        type="radio"
                        :name="`question_${question.id}`"
                        :value="option.value"
                        :disabled="is_question_read_only(sub_section)"
                      >
                      <span>{{ option.label }}</span>
                    </label>
                  </div>

                  <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                    <label
                      v-for="option in question.options"
                      :key="option.id"
                      class="intake-portal-choice"
                    >
                      <input
                        :checked="get_question_value(question).includes(option.value)"
                        type="checkbox"
                        :disabled="is_question_read_only(sub_section)"
                        @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                      >
                      <span>{{ option.label }}</span>
                    </label>
                  </div>

                  <FacialVerificationField
                    v-else-if="uses_facial_verification(question)"
                    :application-form-id="Number(selected_application_form_id || 0)"
                    :question="question"
                    :model-value="get_facial_verification_value(question.id)"
                    @update:modelValue="update_facial_verification_value(question.id, $event)"
                  />

                  <div
                    v-else-if="uses_file(question)"
                    class="intake-portal-file queue-form-file"
                  >
                    <label
                      :for="`question_${question.id}`"
                      class="queue-form-file__chooser"
                    >
                      <input
                        :id="`question_${question.id}`"
                        type="file"
                        class="d-none"
                        :disabled="is_question_read_only(sub_section)"
                        multiple
                        @change="handle_file_change(question.id, $event)"
                      >
                      <span class="intake-portal-file__button">Choose files</span>
                      <span class="intake-portal-file__name">{{ file_input_label(question.id) }}</span>
                    </label>

                    <div class="queue-form-file__actions" v-if="visible_question_files(question.id).length">
                      <div
                        v-for="(file, file_index) in visible_question_files(question.id)"
                        :key="`${question.id}_${file.id || file.name || file_index}`"
                        class="queue-form-file__row"
                      >
                        <span class="queue-form-file__status">
                        <i class="bi bi-check-circle-fill"></i>
                        <span>{{ format_file_name(file) }}</span>
                      </span>
                      <div class="queue-form-file__row-actions">
                        <button
                          type="button"
                          class="queue-form-file__preview-button"
                          @click.prevent="open_preview_modal(question, file)"
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          class="queue-form-file__preview-button"
                          :disabled="is_question_read_only(sub_section)"
                          @click.prevent="remove_question_file(question.id, file_index)"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                  </div>

                  <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                    <input
                      v-model="form_values[question.id]"
                      type="checkbox"
                      :disabled="is_question_read_only(sub_section)"
                    >
                    <span>Yes</span>
                  </label>

                  <input
                    v-else
                    :id="`question_${question.id}`"
                    v-model="form_values[question.id]"
                    :type="question_input_type(question)"
                    class="intake-portal-field__control"
                    :disabled="is_question_read_only(sub_section)"
                  />
                </div>
              </div>

              <template v-else>
                <label
                  :for="`question_${question.id}`"
                  :class="['intake-portal-field__label', { 'intake-portal-field__label--file': uses_file(question) }]"
                >
                  <span>{{ question.label }}<span v-if="question.is_mandatory"> *</span></span>
                  <span v-if="uses_file(question)" class="intake-portal-field__file-cue">
                    <i class="fa-solid fa-plus" aria-hidden="true"></i>
                  </span>
                </label>

                <p v-if="question.description" class="intake-portal-field__hint">{{ question.description }}</p>

                <textarea
                  v-if="uses_textarea(question)"
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  rows="4"
                  class="intake-portal-field__control intake-portal-field__control--textarea"
                  :disabled="is_question_read_only(sub_section)"
                ></textarea>

                <select
                  v-else-if="uses_select(question)"
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  class="intake-portal-field__control"
                  :disabled="is_question_read_only(sub_section)"
                >
                  <option value="">Select an option</option>
                  <option v-for="option in question.options" :key="option.id" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>

                <div v-else-if="uses_radio(question)" class="intake-portal-choice-list">
                  <label
                    v-for="option in question.options"
                    :key="option.id"
                    class="intake-portal-choice"
                  >
                    <input
                      v-model="form_values[question.id]"
                      type="radio"
                      :name="`question_${question.id}`"
                      :value="option.value"
                      :disabled="is_question_read_only(sub_section)"
                    >
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <div v-else-if="uses_checkbox_group(question)" class="intake-portal-choice-list">
                  <label
                    v-for="option in question.options"
                    :key="option.id"
                    class="intake-portal-choice"
                  >
                    <input
                      :checked="get_question_value(question).includes(option.value)"
                      type="checkbox"
                      :disabled="is_question_read_only(sub_section)"
                      @change="update_multi_select_value(question.id, option.value, $event.target.checked)"
                    >
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <FacialVerificationField
                  v-else-if="uses_facial_verification(question)"
                  :application-form-id="Number(selected_application_form_id || 0)"
                  :question="question"
                  :model-value="get_facial_verification_value(question.id)"
                  @update:modelValue="update_facial_verification_value(question.id, $event)"
                />

                <div
                  v-else-if="uses_file(question)"
                  class="intake-portal-file queue-form-file"
                >
                  <label
                    :for="`question_${question.id}`"
                    class="queue-form-file__chooser"
                  >
                    <input
                      :id="`question_${question.id}`"
                      type="file"
                      class="d-none"
                      :disabled="is_question_read_only(sub_section)"
                      multiple
                      @change="handle_file_change(question.id, $event)"
                    >
                    <span class="intake-portal-file__button">Choose files</span>
                    <span class="intake-portal-file__name">{{ file_input_label(question.id) }}</span>
                  </label>

                  <div class="queue-form-file__actions" v-if="visible_question_files(question.id).length">
                    <div
                      v-for="(file, file_index) in visible_question_files(question.id)"
                      :key="`${question.id}_${file.id || file.name || file_index}`"
                      class="queue-form-file__row"
                    >
                      <span class="queue-form-file__status">
                      <i class="bi bi-check-circle-fill"></i>
                      <span>{{ format_file_name(file) }}</span>
                    </span>
                    <div class="queue-form-file__row-actions">
                      <button
                        type="button"
                        class="queue-form-file__preview-button"
                        @click.prevent="open_preview_modal(question, file)"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        class="queue-form-file__preview-button"
                        :disabled="is_question_read_only(sub_section)"
                        @click.prevent="remove_question_file(question.id, file_index)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
                </div>

                <label v-else-if="uses_checkbox(question)" class="intake-portal-choice intake-portal-choice--single">
                  <input
                    v-model="form_values[question.id]"
                    type="checkbox"
                    :disabled="is_question_read_only(sub_section)"
                  >
                  <span>Yes</span>
                </label>

                <input
                  v-else
                  :id="`question_${question.id}`"
                  v-model="form_values[question.id]"
                  :type="question_input_type(question)"
                  class="intake-portal-field__control"
                  :disabled="is_question_read_only(sub_section)"
                />
              </template>
            </div>
          </div>
        </div>
      </div>

      <footer class="intake-portal-card__footer">
        <button
          type="button"
          class="intake-portal-card__back"
          :disabled="current_step === 1 || is_submitting"
          @click="go_to_previous_step"
        >
          Back
        </button>

        <span class="intake-portal-card__step">{{ current_step_label }}</span>

        <button type="submit" class="intake-portal-card__continue" :disabled="is_submitting || !can_submit_current_form">
          <span v-if="!can_submit_current_form">View only</span>
          <span v-else>{{ is_submitting ? 'Saving...' : submit_button_label }}</span>
          <i class="bi bi-arrow-right" aria-hidden="true"></i>
        </button>
      </footer>
    </form>
  </section>

  <section v-else-if="!should_show_workflow_navigation && !is_loading_form_page && is_current_workflow_tab" class="intake-portal-card">
    <header class="intake-portal-card__header">
      <h2 class="intake-portal-card__title">No form available</h2>
      <p class="intake-portal-card__subtitle">
        This applicant does not have an assigned onboarding form yet.
      </p>
    </header>
  </section>

  <div
    v-if="preview_modal_visible"
    class="onboarding-case-detail-backdrop"
    @click="close_preview_modal"
  ></div>

  <section
    v-if="preview_modal_visible"
    class="queue-form-preview-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="preview_modal_title || 'Document preview'"
  >
    <header class="queue-form-preview-modal__header">
      <div>
        <div class="queue-form-preview-modal__eyebrow">Document preview</div>
        <h2 class="queue-form-preview-modal__title">{{ preview_modal_title }}</h2>
      </div>
      <button
        type="button"
        class="queue-form-preview-modal__close"
        aria-label="Close document preview"
        @click="close_preview_modal"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </header>

    <div class="queue-form-preview-modal__body">
      <img
        v-if="preview_modal_type === 'image'"
        :src="preview_modal_src()"
        alt="Selected document preview"
        class="queue-form-preview-modal__image"
      >
      <iframe
        v-else-if="preview_modal_type === 'pdf'"
        :src="preview_modal_src()"
        title="Document preview"
        class="queue-form-preview-modal__frame"
      ></iframe>
      <div v-else class="queue-form-preview-modal__fallback">
        <i class="bi bi-file-earmark-text"></i>
        <p>Preview is not available for this file type.</p>
      </div>
    </div>
  </section>
</main>
